
import { Trash2, MailOpen, Mail, MoreHorizontal, RefreshCw, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface EmailFolderHeaderProps {
  folderName: string;
  emailCount: number;
  selectedEmails: string[];
  loading: boolean;
  currentFolder: string;
  onMarkAsRead: () => void;
  onMarkAsUnread: () => void;
  onDelete: () => void;
  onRefresh: () => void;
  onSelectAll: (selected: boolean) => void;
}

const EmailFolderHeader = ({
  folderName,
  emailCount,
  selectedEmails,
  loading,
  currentFolder,
  onMarkAsRead,
  onMarkAsUnread,
  onDelete,
  onRefresh,
  onSelectAll
}: EmailFolderHeaderProps) => {
  const folderLabels: Record<string, string> = {
    inbox: 'Inbox',
    sent: 'Sent',
    drafts: 'Drafts',
    trash: 'Trash'
  };

  // Only show header if there are selected emails (Gmail style)
  if (selectedEmails.length === 0) {
    return null;
  }

  return (
    <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-blue-900">
            {selectedEmails.length} selected
          </span>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onMarkAsRead}
              className="text-blue-700 hover:bg-blue-100"
            >
              <MailOpen className="h-4 w-4 mr-1" />
              Mark as read
            </Button>
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onMarkAsUnread}
              className="text-blue-700 hover:bg-blue-100"
            >
              <Mail className="h-4 w-4 mr-1" />
              Mark as unread
            </Button>
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onDelete}
              className="text-blue-700 hover:bg-blue-100"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {currentFolder === 'trash' ? 'Delete forever' : 'Delete'}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-blue-700 hover:bg-blue-100">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => onSelectAll(false)}>
                  Clear selection
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onMarkAsRead}>
                  <MailOpen className="h-4 w-4 mr-2" />
                  Mark all as read
                </DropdownMenuItem>
                {currentFolder !== 'trash' && (
                  <DropdownMenuItem>
                    <Archive className="h-4 w-4 mr-2" />
                    Archive
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => onSelectAll(false)}
          className="text-blue-700 hover:bg-blue-100"
        >
          Clear
        </Button>
      </div>
    </div>
  );
};

export default EmailFolderHeader;
